/**
 * <WhyDollar /> — wraps a dollar amount and shows a "Why?" LLM tooltip
 * on hover/focus. Backed by POST /api/why/explain (claude-haiku-4-5 via
 * the Emergent Universal Key).
 *
 * Usage:
 *   <WhyDollar amount={4210} context="Total monthly cost saved by Semantic Cache">
 *     $4,210
 *   </WhyDollar>
 *
 * v34.31 (Feb 2026):
 *   - Tooltip pops on first hover (with a 300ms delay to avoid accidental
 *     trigger when scrolling)
 *   - Fetches once per session per (context, amount) pair — repeat hovers
 *     reuse the already-fetched explanation (backend ALSO caches across
 *     visitors, but the client cache avoids re-roundtripping for the same
 *     visitor browsing the same page)
 *   - Subtle "?" badge next to the children so the affordance is discoverable
 *   - Fully keyboard accessible — focus the trigger to open the tooltip
 */
import React, { useEffect, useRef, useState } from 'react';
import { HelpCircle, Loader2, Sparkles } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Per-session client cache — avoids re-roundtripping for the same (ctx,amount)
const CLIENT_CACHE = new Map();
const cacheKey = (ctx, amt) => `${ctx.trim().toLowerCase()}|${amt}`;

const WhyDollar = ({ children, amount, context, side = 'top', className = '' }) => {
  const [open, setOpen] = useState(false);
  const [explanation, setExplanation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const hoverTimer = useRef(null);
  const triggerRef = useRef(null);

  const fetchExplanation = async () => {
    const key = cacheKey(context, amount);
    if (CLIENT_CACHE.has(key)) {
      setExplanation(CLIENT_CACHE.get(key));
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/api/why/explain`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ context, amount }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const text = data.explanation || 'Reach out to hello@cartieai.com for the per-engine breakdown.';
      CLIENT_CACHE.set(key, text);
      setExplanation(text);
    } catch (e) {
      setError('Reach out to hello@cartieai.com for the per-engine breakdown.');
    } finally {
      setLoading(false);
    }
  };

  const onEnter = () => {
    clearTimeout(hoverTimer.current);
    hoverTimer.current = setTimeout(() => {
      setOpen(true);
      if (!explanation && !loading) fetchExplanation();
    }, 250);
  };

  const onLeave = () => {
    clearTimeout(hoverTimer.current);
    setOpen(false);
  };

  useEffect(() => {
    return () => clearTimeout(hoverTimer.current);
  }, []);

  const sideClasses =
    side === 'bottom'
      ? 'top-full mt-2'
      : side === 'left'
      ? 'right-full mr-2 top-1/2 -translate-y-1/2'
      : side === 'right'
      ? 'left-full ml-2 top-1/2 -translate-y-1/2'
      : 'bottom-full mb-2'; // default 'top'

  return (
    <span
      ref={triggerRef}
      className={`relative inline-flex items-center gap-1 group cursor-help ${className}`}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      onFocus={onEnter}
      onBlur={onLeave}
      tabIndex={0}
      data-testid="why-dollar-trigger"
    >
      <span className="border-b border-dotted border-emerald-400/40 group-hover:border-emerald-300 transition-colors">{children}</span>
      <HelpCircle className="w-3.5 h-3.5 text-emerald-400/60 group-hover:text-emerald-300 transition-colors" aria-label="Why this number?" />

      {open && (
        <span
          role="tooltip"
          className={`absolute ${sideClasses} z-50 left-1/2 -translate-x-1/2 w-72 sm:w-80 rounded-xl border border-emerald-500/30 bg-slate-950/95 backdrop-blur-md shadow-2xl shadow-emerald-500/10 px-4 py-3 pointer-events-none animate-fade-in-up`}
          data-testid="why-dollar-tooltip"
        >
          <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-emerald-300 mb-1.5">
            <Sparkles className="w-3 h-3" />
            Why this number?
          </div>
          {loading ? (
            <div className="flex items-center gap-2 text-xs text-slate-400">
              <Loader2 className="w-3 h-3 animate-spin" />
              Asking the math engine…
            </div>
          ) : (
            <p className="text-[13px] text-slate-200 leading-relaxed">{explanation || error}</p>
          )}
          {/* Small arrow tail */}
          <span
            className={`absolute ${
              side === 'top' ? 'top-full left-1/2 -translate-x-1/2 -mt-px border-t-emerald-500/30 border-x-transparent border-b-transparent border-t-8 border-x-8' : ''
            }`}
            aria-hidden
          />
        </span>
      )}
    </span>
  );
};

export default WhyDollar;
